import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '../../../store/authStore'
import { useOnboardingStore } from '../../../store/onboardingStore'
import { useMessagingStore } from '../../../store/messagingStore'
import { useNotifyPromptStore } from '../../../store/notifyPromptStore'
import { useInstallGuideStore } from '../../../store/installGuideStore'
import { onboardingApi } from '../../../api/onboarding'
import { chatApi, normalizeServerMessage } from '../../../api/chat'
import { useDraftStore, type OnboardingDraft } from './draftStore'
import { analytics } from '../../../lib/analytics'
import { draftVariant } from './onboardingVariant'
import { withLandingQuiz } from './landingQuiz'

export function useOnboardingSubmission() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const running = useRef<{ key: string; promise: Promise<boolean> } | null>(null)
  const lifecycle = useRef({ generation: 0, mounted: false })
  useEffect(() => {
    const owner = lifecycle.current
    owner.mounted = true
    const unsubscribe = useAuthStore.subscribe((next, previous) => {
      if (next.sessionGeneration !== previous.sessionGeneration) owner.generation++
    })
    return () => { owner.mounted = false; unsubscribe() }
  }, [])
  const submit = useCallback((draft: OnboardingDraft): Promise<boolean> => {
    const { userId, sessionGeneration } = useAuthStore.getState()
    if (!userId || draft.ownerUserId !== userId) return Promise.resolve(false)
    const key = `${userId}:${sessionGeneration}:${draft.id}`
    if (running.current?.key === key) return running.current.promise
    const owner = lifecycle.current
    const started = owner.generation
    const current = () => owner.mounted && owner.generation === started && useAuthStore.getState().userId === userId && useAuthStore.getState().sessionGeneration === sessionGeneration
    const execute = async () => {
      setSubmitting(true)
      setError('')
      try {
        const payload = draft.segment === 'break_up' ? await withLandingQuiz(draft.answers) : draft.answers
        if (!current()) return false
        await onboardingApi.submit({ ...payload, custom: { scenario_id: draft.scenarioId, version: draft.version, answers: payload.custom ?? {} } })
        if (!current()) return false
        // the header reads authStore; the profile refetch that would carry
        // the new name only runs on the next login — sync it now so the
        // login email doesn't linger under the logo
        const submittedName = (payload.displayName ?? '').trim()
        if (submittedName) useAuthStore.getState().setDisplayName(submittedName)
        const history = await chatApi.history()
        if (!current()) return false
        useMessagingStore.getState().clear()
        useMessagingStore.getState().setMessages(history.messages.map((message) => normalizeServerMessage(message)))
        const store = useOnboardingStore.getState()
        const first = store.onboarded !== true || store.onboardedForUserId !== userId
        store.setOnboarded(true, userId)
        useDraftStore.getState().clear(draft.id)
        if (first) {
          if (draft.segment === 'break_up') analytics.track('onboardingCompleted', { variant: draftVariant(draft.answers) === 'quiz' ? 'quiz' : 'no_quiz' })
          useNotifyPromptStore.getState().requestShow()
          useInstallGuideStore.getState().requestShow()
        }
        return true
      } catch {
        if (current()) setError('Не удалось завершить знакомство. Ваши ответы сохранены. Попробуйте ещё раз.')
        return false
      } finally {
        if (running.current?.key === key) running.current = null
        if (current()) setSubmitting(false)
      }
    }
    const promise = execute()
    running.current = { key, promise }
    return promise
  }, [])
  return { submit, submitting, error }
}
